import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Image, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';

const DepositDetails = () => {
  const { coin } = useParams();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [network, setNetwork] = useState('ERC20');
  const [amount, setAmount] = useState('');
  const [certificate, setCertificate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);
  const [depositAddresses, setDepositAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const currentCoin = coin.toUpperCase();
  const allowedNetworks =
    currentCoin === 'BTC'
      ? ['BTC']
      : currentCoin === 'ETH'
        ? ['ERC20']
        : currentCoin === 'USDT' || currentCoin === 'USDC'
          ? ['ERC20', 'TRC20']
          : ['ERC20'];

  useEffect(() => {
    if (!allowedNetworks.includes(network)) {
      setNetwork(allowedNetworks[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coin]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch deposit addresses from API
  useEffect(() => {
    const fetchDepositAddresses = async () => {
      try {
        const response = await api.get('/api/financial/deposit-addresses');

        if (response.data?.success && response.data?.data?.addresses) {
          setDepositAddresses(response.data.data.addresses);
        }
      } catch (error) {
        toast.error('Failed to load deposit addresses', {
          position: 'bottom-left',
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDepositAddresses();
  }, []);

  // Get address and QR code for selected coin
  const getDepositInfo = () => {
    const currentCoin = coin.toUpperCase();

    // For USDT and USDC, filter by both currency and network
    if (currentCoin === 'USDT' || currentCoin === 'USDC') {
      const address = depositAddresses.find(
        (addr) => addr.currency === currentCoin && addr.network === network,
      );

      if (address) {
        return {
          address: address.address,
          qrCode: address.qrCode || null,
        };
      }
    } else {
      // For BTC and ETH, just find by currency (no network filter)
      const address = depositAddresses.find(
        (addr) => addr.currency === currentCoin,
      );

      if (address) {
        return {
          address: address.address,
          qrCode: address.qrCode || null,
        };
      }
    }

    // Return loading state if no data found
    return {
      address: loading ? 'Loading...' : 'Not available',
      qrCode: null,
    };
  };

  const depositInfo = getDepositInfo();

  const handleConfirm = async () => {
    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid deposit amount', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    if (!certificate) {
      toast.error('Please upload the recharge voucher', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    try {
      setSubmitting(true);

      // Create FormData to send file and other data
      const formData = new FormData();
      const currentCoin = coin.toUpperCase();

      formData.append('currencyCode', currentCoin);
      formData.append('amount', parseFloat(amount));

      // For USDT and USDC, include network in method (USDT_TRC20, USDT_ERC20, etc.)
      // For BTC and ETH, just use the coin name
      if (currentCoin === 'USDT' || currentCoin === 'USDC') {
        formData.append('method', `${currentCoin}_${network}`);
      } else {
        formData.append('method', currentCoin);
      }

      formData.append('walletAddress', depositInfo.address);
      formData.append('depositProof', certificate);

      // Submit deposit request
      // Don't set Content-Type manually - let browser set it with boundary
      const response = await api.post('/api/financial/deposit', formData);

      if (response.data?.success) {
        toast.success(
          response.data.message || 'Deposit request submitted successfully!',
          {
            position: 'bottom-left',
            autoClose: 3000,
          },
        );

        // Clear form
        setAmount('');
        setCertificate(null);
        setPreview(null);

        // Navigate to deposit records after a short delay
        setTimeout(() => {
          navigate('/deposit-records');
        }, 1500);
      } else {
        toast.error(response.data?.message || 'Failed to submit deposit', {
          position: 'bottom-left',
          autoClose: 3000,
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to submit deposit request';

      toast.error(errorMessage, {
        position: 'bottom-left',
        autoClose: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      fluid
      style={{
        maxWidth: isMobile ? '100%' : '85%',
        padding: 'clamp(1rem, 3vw, 1.5rem)',
      }}
    >
      {/* Header */}
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <div className='d-flex align-items-center'>
          <Button
            variant='link'
            onClick={() => navigate('/deposit')}
            className='text-dark p-0 me-2'
          >
            <i className='bi bi-arrow-left fs-5'></i>
          </Button>
          <h5 className='mb-0'>{coin.toUpperCase()} Recharge</h5>
        </div>
        <Button
          variant='link'
          onClick={() => navigate('/deposit-records')}
          className='text-dark p-0'
        >
          <i className='bi bi-clock fs-5'></i>
        </Button>
      </div>

      {/* Coin and Network Select */}
      <div className='bg-white p-3 rounded shadow-sm mb-3'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <strong>{coin.toUpperCase()}</strong>
        </div>
        <div className='d-flex gap-2'>
          {allowedNetworks.map((net) => (
            <Button
              key={net}
              style={{
                backgroundColor: network === net ? '#2F80ED' : 'transparent',
                color: network === net ? 'white' : '#6c757d',
                border: '1px solid #2F80ED',
              }}
              onClick={() => setNetwork(net)}
            >
              {net}
            </Button>
          ))}
        </div>
      </div>

      <h6>Money information</h6>
      <div className='bg-white p-3 rounded shadow-sm d-flex flex-column align-items-center text-center mb-3'>
        {loading ? (
          <div className='py-5'>
            <Spinner animation='border' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            {depositInfo.qrCode ? (
              <Image
                src={depositInfo.qrCode}
                alt='QR Code'
                width={180}
                height={180}
                className='mb-3'
                style={{ objectFit: 'contain' }}
              />
            ) : (
              <div
                className='mb-3 d-flex align-items-center justify-content-center'
                style={{ width: 180, height: 180, backgroundColor: '#f0f0f0' }}
              >
                <span className='text-muted'>No QR Code</span>
              </div>
            )}
            <div className='text-muted small'>Recharge address</div>
            <div className='small mb-2' style={{ wordBreak: 'break-all' }}>
              {depositInfo.address}
            </div>
            <Button
              size='sm'
              style={{
                backgroundColor: '#2F80ED',
                borderColor: '#2F80ED',
                color: '#fff',
              }}
              onClick={() => {
                navigator.clipboard.writeText(depositInfo.address);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              disabled={
                depositInfo.address === 'Loading...' ||
                depositInfo.address === 'Not available'
              }
            >
              {copied ? 'Copied successfully' : 'Copy Address'}
            </Button>
          </>
        )}
      </div>

      {/* Deposit Amount */}
      <Form.Group className='mb-3'>
        <Form.Label>Number of deposits</Form.Label>
        <Form.Control
          type='number'
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder='Please enter the recharge amount'
          min='0'
          step='any'
          disabled={submitting}
        />
      </Form.Group>

      {/* Upload Certificate */}
      <Form.Group className='mb-3'>
        <Form.Label>Upload certificate</Form.Label>
        <Form.Control
          type='file'
          accept='image/*'
          style={{ display: 'none' }}
          id='upload-certificate'
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              setCertificate(file);
              setPreview(URL.createObjectURL(file));
            }
          }}
        />
        <div
          className='text-white text-center rounded p-5 d-flex flex-column align-items-center justify-content-center'
          style={{
            cursor: 'pointer',
            minHeight: '150px',
            backgroundColor: '#2F80ED',
          }}
          onClick={() => document.getElementById('upload-certificate').click()}
        >
          {preview ? (
            <Image
              src={preview}
              alt='Uploaded'
              thumbnail
              style={{
                maxWidth: '100px',
                maxHeight: '100px',
                objectFit: 'contain',
              }}
            />
          ) : (
            <>
              <div style={{ fontSize: '24px' }}>+</div>
              <div>Click to upload picture</div>
            </>
          )}
        </div>
      </Form.Group>

      <Button
        className='w-100 mb-3'
        style={{ backgroundColor: '#2F80ED', borderColor: '#2F80ED' }}
        onClick={handleConfirm}
        disabled={submitting || loading}
      >
        {submitting ? (
          <>
            <Spinner
              as='span'
              animation='border'
              size='sm'
              role='status'
              aria-hidden='true'
              className='me-2'
            />
            Processing...
          </>
        ) : (
          'Confirm the recharge'
        )}
      </Button>

      <div className='small text-muted'>
        <p>
          Do not recharge any non-currency assets from the above address,
          otherwise the assets will not be retrieved.
        </p>
        <p>
          After you recharge to the above address, you need to confirm the
          entire blockchain network node.
        </p>
        <p>Minimum recharge amount: 0 {coin.toUpperCase()}</p>
        <p>
          Your recharge address will not change frequently. If there are
          changes, we will notify you via announcements or email.
        </p>
      </div>
    </Container>
  );
};

export default DepositDetails;
